// Tabs (drawing v2.6, `.tabs`; reference section 7): underline tabs, the parts
// of one page. 44 tall, 24 apart; the one that is on is bold with a 2px navy
// underline (dark: the link blue); a count shows as a small amber pill. Under
// 1024 the strip scrolls inside itself. A view switch (the same data another
// way) is ViewSwitch, not this.
//
// Accessibility: role="tablist" with roving tabindex; arrows move, Home and
// End jump; each tab is paired with its panel by id.

import { useRef, type ReactNode } from "react";
import { cx } from "./cx";

export interface TabItem {
  id: string;
  label: ReactNode;
  count?: number;
}

export function tabId(base: string, id: string): string {
  return `${base}-tab-${id}`;
}
export function tabPanelId(base: string, id: string): string {
  return `${base}-panel-${id}`;
}

export function Tabs({ tabs, active, onChange, label, idBase = "tabs", className }: { tabs: TabItem[]; active: string; onChange: (id: string) => void; label: string; idBase?: string; className?: string }) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  function focusTab(i: number) {
    const next = (i + tabs.length) % tabs.length;
    refs.current[next]?.focus();
    const t = tabs[next];
    if (t) onChange(t.id);
  }
  return (
    <div className={cx("pg-tabs", className)} role="tablist" aria-label={label}>
      {tabs.map((t, i) => {
        const on = t.id === active;
        return (
          <button
            key={t.id}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={tabId(idBase, t.id)}
            aria-selected={on}
            aria-controls={tabPanelId(idBase, t.id)}
            tabIndex={on ? 0 : -1}
            className={cx("pg-tab", on && "is-on")}
            onClick={() => onChange(t.id)}
            onKeyDown={(e) => {
              if (e.key === "ArrowRight" || e.key === "ArrowDown") {
                e.preventDefault();
                focusTab(i + 1);
              } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
                e.preventDefault();
                focusTab(i - 1);
              } else if (e.key === "Home") {
                e.preventDefault();
                focusTab(0);
              } else if (e.key === "End") {
                e.preventDefault();
                focusTab(tabs.length - 1);
              }
            }}
          >
            {t.label}
            {t.count != null && t.count > 0 && <small className="pg-tab__count">{t.count}</small>}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({ id, idBase = "tabs", children, className }: { id: string; idBase?: string; children: ReactNode; className?: string }) {
  return (
    <div role="tabpanel" id={tabPanelId(idBase, id)} aria-labelledby={tabId(idBase, id)} tabIndex={0} className={className}>
      {children}
    </div>
  );
}
